import Link from 'next/link'

import { Box, Divider, Flex, HStack, Popover, Skeleton } from '@chakra-ui/react'

import { ClickToCopyPopover } from '@/components/layouts/Copy/ClickToCopyPopover'
import { PopoverTrigger } from '@/components/layouts/Copy/PopoverTrigger'

interface DrawerBreadcrumbIProps {
  dataTestId?: string
  disabled?: boolean
  hideDivider?: boolean
  href?: string
  label: string
  isLoading: boolean
  onClick?: () => void
  onClose?: () => void
  tooltipLabel?: string
  isTruncated?: boolean
  maxWidth: string | number
  fontSize?: string
}

// Space (in px) reserved for the navigation chevron icon when breadcrumb is shown alongside nav controls
const ICON_OFFSET_PX = 14

const computeLabelMaxWidth = (maxWidth: string | number): string => {
  const base = typeof maxWidth === 'number' ? maxWidth : parseInt(maxWidth, 10)
  if (isNaN(base)) {
    return typeof maxWidth === 'string' ? maxWidth : `${maxWidth}px`
  }
  return `${Math.max(base - ICON_OFFSET_PX, 0)}px`
}

export const DrawerBreadcrumb = ({
  dataTestId,
  disabled = false,
  hideDivider = false,
  href,
  isTruncated = true,
  fontSize = '16px',
  maxWidth,
  label,
  isLoading,
  onClick,
  onClose,
  tooltipLabel,
}: DrawerBreadcrumbIProps) => {
  if (!label && !isLoading) {
    return null
  }

  const isLink = onClick !== undefined || href !== undefined
  const isActiveLink = isLink && !disabled
  const isAnchor = href !== undefined && !disabled
  const labelMaxWidth: string = computeLabelMaxWidth(maxWidth)

  const labelColor = disabled ? 'brand.600' : isLink ? 'link.100' : 'inherit'

  const handleClick = (e: React.MouseEvent) => {
    if (href) {
      // Modifier / middle-click: let the browser open in a new tab without
      // running any in-tab side effects on the current page — source drawer
      // stays open, matching standard browser link behavior.
      if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) return
      // Plain click with an href: close the source drawer and run the
      // caller-owned in-tab navigation. preventDefault avoids a redundant
      // router.push from Link's default handler.
      onClose?.()
      if (onClick) {
        e.preventDefault()
        onClick()
      }
      return
    }
    onClose?.()
    onClick?.()
  }

  const labelStyleProps = {
    _hover: isActiveLink ? { textDecoration: 'underline' } : undefined,
    color: labelColor,
    cursor: isActiveLink ? 'pointer' : 'auto',
    'data-testid': dataTestId,
    fontSize,
    fontWeight: 'semibold',
    isTruncated,
    letterSpacing: '-0.53px',
    maxW: labelMaxWidth,
    onClick: isActiveLink ? handleClick : undefined,
    px: '7px',
  }

  return (
    <HStack isTruncated={isTruncated} maxWidth={maxWidth}>
      {isLoading ? (
        <Skeleton h='16px' w='40px' />
      ) : (
        // zIndex is provided to ensure popover goes above videos value of 9999 was
        // copied from existing device health popover.
        <Flex zIndex='9999'>
          <Popover
            closeDelay={250}
            isLazy
            openDelay={750}
            placement='bottom'
            trigger='hover'
          >
            <PopoverTrigger>
              {isAnchor && href ? (
                <Box
                  as={Link}
                  href={href}
                  textDecoration='none'
                  {...labelStyleProps}
                >
                  {label}
                </Box>
              ) : (
                <Box {...labelStyleProps}>{label}</Box>
              )}
            </PopoverTrigger>
            <ClickToCopyPopover
              displayText={tooltipLabel ? tooltipLabel : label}
            />
          </Popover>
        </Flex>
      )}
      {!hideDivider && (
        <Divider
          borderColor='#D7D4D4'
          borderLeftWidth='2px'
          h='20px'
          orientation='vertical'
        />
      )}
    </HStack>
  )
}
